export type ClinicalSide = 'left' | 'right' | 'bilateral' | 'unknown';
export type AmplitudeUnit = 'mV' | 'µV' | 'unknown';

const NERVE_CANONICAL_MAP: Record<string, string> = {
  median: 'median',
  mediano: 'median',
  ulnar: 'ulnar',
  cubital: 'ulnar',
  radial: 'radial',
  tibial: 'tibial',
  sural: 'sural',
  peroneal: 'peroneal',
  peroneo: 'peroneal',
  fibular: 'peroneal',
  superficial_peroneal: 'superficial_peroneal',
  'peroneo superficial': 'superficial_peroneal',
  'superficial peroneal': 'superficial_peroneal'
};

const NERVE_DISPLAY_MAP: Record<string, string> = {
  median: 'Mediano',
  ulnar: 'Ulnar',
  radial: 'Radial',
  tibial: 'Tibial',
  sural: 'Sural',
  peroneal: 'Peroneo',
  superficial_peroneal: 'Peroneo Superficial'
};

const MUSCLE_DISPLAY_MAP: Record<string, string> = {
  gastrocnemius: 'Gastrocnemius',
  gastrocnemio: 'Gastrocnemius',
  'peroneus longus': 'Peroneus Longus',
  semitendinosus: 'Semitendinosus',
  'tibialis anterior': 'Tibialis Anterior',
  'tibial anterior': 'Tibialis Anterior',
  'biceps femoris (short head)': 'Biceps Femoris (Short Head)',
  'biceps femoris': 'Biceps Femoris',
  'vastus lateralis': 'Vastus Lateralis',
  'vastus medialis': 'Vastus Medialis',
  deltoid: 'Deltoid',
  deltoides: 'Deltoid',
  triceps: 'Triceps',
  'biceps brachii': 'Biceps Brachii',
  'biceps brachi.': 'Biceps Brachii'
};

export function normalizeClinicalText(value: string): string {
  return (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/µ/g, 'u')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ');
}

export function stripLateralitySuffix(value: string): string {
  return (value || '').trim().replace(/\.(i|d|izq|der|left|right)$/i, '').trim();
}

export function detectSide(value: string): ClinicalSide {
  const raw = (value || '').trim();
  const normalized = normalizeClinicalText(raw);
  if (/\.(i|izq|left)\b/i.test(raw) || /\b(left|izquierd[ao]|izq)\b/.test(normalized)) return 'left';
  if (/\.(d|der|right)\b/i.test(raw) || /\b(right|derech[ao]|der)\b/.test(normalized)) return 'right';
  if (/\b(bilateral|ambos|ambas)\b/.test(normalized)) return 'bilateral';
  return 'unknown';
}

export function canonicalNerveName(value: string): string {
  const normalized = normalizeClinicalText(stripLateralitySuffix(value)).replace(/[-_]/g, ' ');
  for (const [key, canonical] of Object.entries(NERVE_CANONICAL_MAP)) {
    if (normalized === key || normalized.includes(key)) return canonical;
  }
  return normalized;
}

export function displayNerveName(value: string): string {
  const canonical = canonicalNerveName(value);
  if (NERVE_DISPLAY_MAP[canonical]) return NERVE_DISPLAY_MAP[canonical];
  return titleCase(stripLateralitySuffix(value).replace(/[-_]/g, ' '));
}

export function displayMuscleName(value: string): string {
  const cleaned = stripLateralitySuffix(value);
  const normalized = normalizeClinicalText(cleaned);
  if (MUSCLE_DISPLAY_MAP[normalized]) return MUSCLE_DISPLAY_MAP[normalized];
  return titleCase(cleaned.replace(/[-_]/g, ' '));
}

export function detectAmplitudeUnit(value: string, fallbackType?: 'motor' | 'sensory'): AmplitudeUnit {
  const normalized = normalizeClinicalText(value);
  if (/\b(mv|millivolt)/.test(normalized)) return 'mV';
  if (/\b(uv|microv|microvolt)/.test(normalized)) return 'µV';
  if (fallbackType === 'motor') return 'mV';
  if (fallbackType === 'sensory') return 'µV';
  return 'unknown';
}

function titleCase(value: string): string {
  return value
    .trim()
    .replace(/\s+/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}
